import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Collapse from '@mui/material/Collapse'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ChatIcon from '@mui/icons-material/Chat'
import UsersIcon from '@mui/icons-material/People'
import NetworksIcon from '@mui/icons-material/Hub'
import StaffIcon from '@mui/icons-material/Badge'
import SettingsIcon from '@mui/icons-material/Settings'
import SearchIcon from '@mui/icons-material/Search'
// import VectorSearchIcon from '@mui/icons-material/ShareLocation'
import TranscriptionIcon from '@mui/icons-material/InterpreterMode'
import SwaggerIcon from '@mui/icons-material/Description'
import GPTIcon from '@mui/icons-material/Psychology'
import TagsIcon from '@mui/icons-material/CloudOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import SchoolsIcon from '@mui/icons-material/School'
import TeamReclipStoryIcon from '@mui/icons-material/Mediation'
import ChatbotsIcon from '@mui/icons-material/SmartToy'
import Divider from '@mui/material/Divider'
import AnimationStylesIcon from '@mui/icons-material/MovieFilter'
import KillSwitchIcon from '@mui/icons-material/MoneyOff'
import HeartbeatsIcon from '@mui/icons-material/MonitorHeart'
import OverviewIcon from '@mui/icons-material/Insights'
import PipelineIcon from '@mui/icons-material/GraphicEq'
import LoopIcon from '@mui/icons-material/AllInclusive'
import MomentsIcon from '@mui/icons-material/FormatQuote'
import ReportsIcon from '@mui/icons-material/OutlinedFlag'
import ToolsIcon from '@mui/icons-material/Build'
import { useNavigate } from 'react-router-dom'
import useSettingsActions from 'store/actions/settings'
import { useSettings } from 'store/selectors'
import useAuthActions from 'store/actions/auth'
import EnvironmentToggle from './EnvironmentToggle'
import { CHAT_ONLY_USER_IDS, TOOLS_ONLY_USER_IDS } from 'const'
import { useAuth } from 'store/selectors'

const CHAT_ONLY_ITEMS = [
  {
    path: '/chat',
    title: 'Chat',
    Icon: ChatIcon,
  },
]

const TOOLS_ONLY_ITEMS = [
  {
    path: '/tools',
    title: 'Tools',
    Icon: ToolsIcon,
  },
]

const ITEMS = [
  {
    path: '/chat',
    title: 'Chat',
    Icon: ChatIcon,
  },
  {
    path: '/users',
    title: 'Users',
    Icon: UsersIcon,
  },
  // {
  //   path: '/map',
  //   title: 'Recent Clips',
  //   Icon: MapIcon,
  // },
  {
    path: '/staff',
    title: 'Staff Members',
    Icon: StaffIcon,
  },
  {
    path: '/swagger',
    title: 'Swagger',
    Icon: SwaggerIcon,
  },
  {
    path: '/search',
    title: 'Search',
    Icon: SearchIcon,
  },
  {
    path: '/moments',
    title: 'Moments',
    Icon: MomentsIcon,
  },
  {
    path: '/reports',
    title: 'Reports',
    Icon: ReportsIcon,
  },
  {
    path: '/heartbeats',
    title: 'Heartbeats',
    Icon: HeartbeatsIcon,
  },
  {
    path: '/overview',
    title: 'Overview',
    Icon: OverviewIcon,
  },
  {
    path: '/pipeline',
    title: 'Pipeline',
    Icon: PipelineIcon,
  },
  {
    path: '/loop',
    title: 'Loop',
    Icon: LoopIcon,
  },
  // {
  //   path: '/vector-search',
  //   title: 'Vector Search',
  //   Icon: VectorSearchIcon,
  // },
  {
    path: '/cost',
    title: 'Cost',
    Icon: KillSwitchIcon,
  },
]

// Less-used pages, tucked behind the "More" chevron after Cost.
const MORE_ITEMS = [
  {
    path: '/tools',
    title: 'Tools',
    Icon: ToolsIcon,
  },
  {
    path: '/gpt',
    title: 'AI',
    Icon: GPTIcon,
  },
  {
    path: '/team-reclip-story',
    title: 'Team Reclip Story',
    Icon: TeamReclipStoryIcon,
  },
  {
    path: '/animation-styles',
    title: 'Animation Styles',
    Icon: AnimationStylesIcon,
  },
  {
    path: '/networks',
    title: 'Networks',
    Icon: NetworksIcon,
  },
  {
    path: '/schools',
    title: 'Schools',
    Icon: SchoolsIcon,
  },
  {
    path: '/chatbots',
    title: 'Chatbots',
    Icon: ChatbotsIcon,
  },
  {
    path: '/transcription',
    title: 'Transcription',
    Icon: TranscriptionIcon,
  },
  {
    path: '/tags',
    title: 'Tags',
    Icon: TagsIcon,
  },
]

// The pages a phone's tab bar holds (TabBar.js), in its order. The rest are
// under its More.
const TAB_PATHS = ['/chat', '/users', '/moments', '/overview']

// The nav each kind of staff member sees: every page, the rarely used ones
// the drawer tucks behind its chevron, and a phone's tabs. Restricted staff
// see their one page.
const NAV = {
  items: ITEMS,
  moreItems: MORE_ITEMS,
  tabs: TAB_PATHS.map((path) => ITEMS.find((item) => item.path === path)),
}
const CHAT_ONLY_NAV = {
  items: CHAT_ONLY_ITEMS,
  moreItems: [],
  tabs: CHAT_ONLY_ITEMS,
}
const TOOLS_ONLY_NAV = {
  items: TOOLS_ONLY_ITEMS,
  moreItems: [],
  tabs: TOOLS_ONLY_ITEMS,
}

export const useNavItems = () => {
  const { user } = useAuth()
  if (CHAT_ONLY_USER_IDS.includes(user.id)) return CHAT_ONLY_NAV
  if (TOOLS_ONLY_USER_IDS.includes(user.id)) return TOOLS_ONLY_NAV
  return NAV
}

// The pages a phone's More lists under Recent: the last three visited that
// the tabs leave out, newest first, kept on this device. Storage is read and
// written in a try, since a private window can refuse it; Recent then stays
// as it was.
const RECENT_KEY = 'nav.recent'

const readRecent = () => {
  try {
    const paths = JSON.parse(window.localStorage.getItem(RECENT_KEY))
    return Array.isArray(paths) ? paths : []
  } catch {
    return []
  }
}

// Notes the page on view as a recent one; the Layout's, on each navigation.
export const useNoteRecent = () => {
  const { pathname } = useLocation()
  const { items, moreItems, tabs } = useNavItems()
  useEffect(() => {
    const item = [...items, ...moreItems].find(({ path }) =>
      pathname.startsWith(path)
    )
    if (!item || tabs.includes(item)) return
    const paths = [
      item.path,
      ...readRecent().filter((path) => path !== item.path),
    ].slice(0, 3)
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(paths))
    } catch {
      // Refused: Recent stays as it was.
    }
  }, [pathname, items, moreItems, tabs])
}

export const useRecentItems = () => {
  const { items, moreItems, tabs } = useNavItems()
  const all = [...items, ...moreItems]
  return readRecent()
    .map((path) => all.find((item) => item.path === path))
    .filter((item) => item && !tabs.includes(item))
}

export const NavItem = ({ path, title, Icon }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const active = pathname.startsWith(path)

  return (
    <ListItemButton
      sx={{
        backgroundColor: active ? 'action.focus' : undefined,
        cursor: active ? 'default' : undefined,
        '&:hover': {
          backgroundColor: active ? 'action.focus' : undefined,
        },
      }}
      onClick={active ? undefined : navigate.bind(null, path)}
    >
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      <ListItemText primary={title} />
    </ListItemButton>
  )
}

export const MainListItems = () => {
  const { pathname } = useLocation()
  const { items, moreItems } = useNavItems()

  // Start expanded when the current page lives behind the chevron, so
  // its active highlight isn't hidden.
  const [moreOpen, setMoreOpen] = useState(() =>
    moreItems.some(({ path }) => pathname.startsWith(path))
  )

  // Also expand when navigation lands on a tucked-away page after mount
  // (deep link within the SPA, browser back/forward). Never auto-collapse
  // — leaving is the user's call.
  useEffect(() => {
    if (moreItems.some(({ path }) => pathname.startsWith(path)))
      setMoreOpen(true)
  }, [pathname, moreItems])

  if (!moreItems.length)
    return items.map((item) => <NavItem key={item.path} {...item} />)

  return (
    <>
      {items.map((item) => (
        <NavItem key={item.path} {...item} />
      ))}
      <ListItemButton onClick={() => setMoreOpen(!moreOpen)}>
        <ListItemIcon>
          {moreOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemIcon>
        <ListItemText primary={moreOpen ? 'Less' : 'More'} />
      </ListItemButton>
      <Collapse in={moreOpen} timeout="auto">
        {moreItems.map((item) => (
          <NavItem key={item.path} {...item} />
        ))}
      </Collapse>
    </>
  )
}

export const SecondaryListItems = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { toggleTheme } = useSettingsActions()
  const { theme } = useSettings()
  const { logout } = useAuthActions()
  const { user } = useAuth()

  // Restricted users have no Settings page, so keep Log Out in the sidebar for
  // them. Full-access users get Settings here (Log Out lives inside Settings).
  const isRestricted = (CHAT_ONLY_USER_IDS + TOOLS_ONLY_USER_IDS).includes(
    user.id
  )
  const settingsActive = pathname.startsWith('/settings')

  return (
    <>
      {!isRestricted && <EnvironmentToggle />}
      <Divider sx={{ marginBottom: 1 }} />
      <ListItemButton onClick={toggleTheme}>
        <ListItemIcon>
          {theme === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
        </ListItemIcon>
        <ListItemText
          primary={theme === 'light' ? 'Dark Mode' : 'Light Mode'}
        />
      </ListItemButton>
      {!isRestricted && (
        <ListItemButton
          sx={{
            backgroundColor: settingsActive ? 'action.focus' : undefined,
            cursor: settingsActive ? 'default' : undefined,
            '&:hover': {
              backgroundColor: settingsActive ? 'action.focus' : undefined,
            },
          }}
          onClick={
            settingsActive ? undefined : navigate.bind(null, '/settings')
          }
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItemButton>
      )}
      {isRestricted && (
        <ListItemButton onClick={logout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Log Out" />
        </ListItemButton>
      )}
    </>
  )
}
